import React, { useState, useEffect, useRef } from 'react';
import DancingDotsProgress from '../components/ui/DancingDotsProgress';
import { nativeDownload } from '../utils/nativeDownload';

const Badges = ({ achievements, trackInteraction, fetchWithAuth }) => {
    const [filter, setFilter] = useState('all'); // 'all', 'unlocked', 'locked'
    const [selectedBadge, setSelectedBadge] = useState(null);
    const [downloadingCert, setDownloadingCert] = useState(false);

    // Track BADGE_VIEW wenn ein Badge ausgewählt wird
    // Track Badge-View nur wenn sich selectedBadge ändert
    const lastTrackedBadgeId = useRef(null);
    useEffect(() => {
        if (selectedBadge && selectedBadge.id !== lastTrackedBadgeId.current && trackInteraction) {
            lastTrackedBadgeId.current = selectedBadge.id;
            trackInteraction('BADGE_VIEW', {
                badge_id: selectedBadge.id,
                badge_name: selectedBadge.name,
                unlocked: selectedBadge.unlocked,
                progress: selectedBadge.progress
            });
        }
    }, [selectedBadge, trackInteraction]); // Nur tracken wenn sich selectedBadge aendert

    if (!achievements || !achievements.achievements) {
        return (
            <div className="p-3 sm:p-4 md:p-6 lg:p-8 max-w-7xl mx-auto w-full">
                <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 sm:p-8 text-center border border-gray-100 dark:border-gray-800">
                    <div className="animate-spin rounded-full h-10 w-10 border-t border-b border-gray-300 dark:border-gray-600 mx-auto mb-3"></div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Lade Badges...</p>
                </div>
            </div>
        );
    }

    const allAchievements = achievements.achievements || [];
    const unlockedCount = allAchievements.filter(a => a.unlocked).length;
    const lockedCount = allAchievements.length - unlockedCount;

    const filteredAchievements = filter === 'all'
        ? allAchievements
        : filter === 'unlocked'
            ? allAchievements.filter(a => a.unlocked)
            : allAchievements.filter(a => !a.unlocked);

    return (
        <div className="p-3 sm:p-4 md:p-6 lg:p-8 max-w-7xl mx-auto w-full">
            {/* Header mit Level & XP */}
            {achievements && achievements.xp && (
                <div className="mb-8 pb-6 border-b border-gray-100 dark:border-gray-800">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div
                            className="flex items-center space-x-3 cursor-pointer hover:opacity-70 transition-opacity"
                            onClick={() => {
                                // Filter auf alle freigeschalteten Badges setzen
                                setFilter('unlocked');
                                // Scroll zu den Badges
                                setTimeout(() => {
                                    document.querySelector('.badges-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }, 100);
                            }}
                            title="Klicken um freigeschaltete Badges anzuzeigen"
                        >
                            <div className="text-xl text-gray-400 dark:text-gray-600">
                                ⭐
                            </div>
                            <div>
                                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Level {achievements.xp.level || 1}</h1>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{achievements.xp.total || 0} XP</p>
                            </div>
                        </div>
                        <div
                            className="w-full sm:w-56 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => {
                                // Zeige alle Badges an
                                setFilter('all');
                            }}
                            title="Klicken um alle Badges anzuzeigen"
                        >
                            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                                <span>→ Level {(achievements.xp.level || 1) + 1}</span>
                                <span className="font-medium">{(achievements.xp.xp_for_current_level || 0)} / {(achievements.xp.xp_for_next_level || 100)} XP</span>
                            </div>
                            <DancingDotsProgress
                                percentage={Math.max(0, Math.min(100, achievements.xp.progress_percentage || 0))}
                                height="h-1"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Filter Buttons */}
            <div className="mb-8 flex flex-wrap gap-2">
                <button
                    onClick={() => setFilter('all')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${filter === 'all'
                        ? 'bg-plantlix-500 text-white shadow-sm'
                        : 'bg-white dark:bg-[#1a1a1a] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#242424] border border-gray-200 dark:border-gray-800'
                        }`}
                >
                    Alle ({allAchievements.length})
                </button>
                <button
                    onClick={() => setFilter('unlocked')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${filter === 'unlocked'
                        ? 'bg-plantlix-500 text-white shadow-sm'
                        : 'bg-white dark:bg-[#1a1a1a] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#242424] border border-gray-200 dark:border-gray-800'
                        }`}
                >
                    Freigeschaltet ({unlockedCount})
                </button>
                <button
                    onClick={() => setFilter('locked')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${filter === 'locked'
                        ? 'bg-plantlix-500 text-white shadow-sm'
                        : 'bg-white dark:bg-[#1a1a1a] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#242424] border border-gray-200 dark:border-gray-800'
                        }`}
                >
                    Gesperrt ({lockedCount})
                </button>
            </div>

            {/* Badges Grid */}
            <div className="badges-grid grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                {filteredAchievements.map((ach) => (
                    <div
                        key={ach.id}
                        onClick={(e) => {
                            // Verhindere Klick wenn auf Checkmark geklickt wird
                            if (e.target.closest('.unlock-checkmark')) return;
                            setSelectedBadge(ach);
                        }}
                        className={`group relative p-4 rounded-lg border transition-all duration-200 cursor-pointer hover:border-gray-300 dark:hover:border-gray-600 active:scale-95 ${ach.unlocked
                            ? 'bg-white dark:bg-[#1a1a1a] border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-[#1e1e1e]'
                            : 'bg-gray-50 dark:bg-[#1e1e1e] border-gray-100 dark:border-gray-800 opacity-60 hover:opacity-80'
                            }`}
                        title={ach.description}
                    >
                        {/* Badge Content */}
                        <div className="text-center">
                            <div className={`text-4xl mb-2 ${ach.unlocked ? '' : 'grayscale opacity-40'}`}>
                                {ach.icon}
                            </div>

                            <h3 className={`text-xs font-medium mb-1 ${ach.unlocked ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-500'}`}>
                                {ach.name}
                            </h3>

                            {ach.unlocked ? (
                                <div className="flex items-center justify-between gap-1 w-full mt-1.5 px-0.5">
                                    <div
                                        className="text-[10px] font-black text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedBadge(ach);
                                        }}
                                    >
                                        +{ach.xp_reward} XP
                                    </div>
                                    <button
                                        title="Zertifikat herunterladen"
                                        className="p-1.5 bg-gray-50 dark:bg-gray-800 hover:bg-plantlix-100 dark:hover:bg-plantlix-900/30 rounded-lg transition-all active:scale-90 flex items-center justify-center shadow-sm"
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            if (downloadingCert) return;
                                            setDownloadingCert(true);
                                            const token = localStorage.getItem('authToken');
                                            await nativeDownload(
                                                `/api/profile/certificate/achievement/${ach.id}/`,
                                                token,
                                                (msg) => alert(msg)
                                            );
                                            setDownloadingCert(false);
                                        }}
                                    >
                                        <span className="text-[12px]">📜</span>
                                    </button>
                                </div>
                            ) : (
                                <div className="mt-1">
                                    {ach.progress > 0 && (
                                        <div
                                            className="w-full cursor-pointer hover:opacity-80 transition-opacity"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedBadge(ach);
                                            }}
                                            title={`Fortschritt: ${ach.progress}%`}
                                        >
                                            <DancingDotsProgress
                                                percentage={ach.progress}
                                                height="h-1"
                                            />
                                        </div>
                                    )}
                                    {ach.progress === 0 && (
                                        <p
                                            className="text-xs text-gray-500 dark:text-gray-500 cursor-pointer hover:text-gray-700 dark:hover:text-gray-400 transition-colors"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedBadge(ach);
                                            }}
                                        >
                                            Noch nicht gestartet
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Unlock Indicator - Klickbar */}
                        {ach.unlocked && (
                            <div
                                className="unlock-checkmark absolute top-1.5 right-1.5 w-4 h-4 bg-gray-400 dark:bg-gray-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-500 dark:hover:bg-gray-400 transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedBadge(ach);
                                }}
                                title="Badge-Details anzeigen"
                            >
                                <span className="text-[8px] text-white">✓</span>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Empty State */}
            {filteredAchievements.length === 0 && (
                <div className="text-center py-12">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Keine Badges gefunden</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Versuche einen anderen Filter</p>
                    <button
                        onClick={() => setFilter('all')}
                        className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 underline transition-colors"
                    >
                        Alle Badges anzeigen
                    </button>
                </div>
            )}

            {/* Badge Detail Modal */}
            {selectedBadge && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30"
                    onClick={() => setSelectedBadge(null)}
                >
                    <div
                        className="relative max-w-sm w-full p-6 rounded-lg bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800"
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setSelectedBadge(null)}
                            className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="text-center">
                            <div className={`text-5xl mb-3 ${selectedBadge.unlocked ? '' : 'grayscale opacity-40'}`}>
                                {selectedBadge.icon}
                            </div>

                            <h2 className={`text-lg font-semibold mb-2 ${selectedBadge.unlocked ? 'text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>
                                {selectedBadge.name}
                            </h2>

                            <p className={`text-xs mb-5 text-gray-600 dark:text-gray-400`}>
                                {selectedBadge.description}
                            </p>

                            {selectedBadge.unlocked ? (
                                <div className="space-y-2">
                                    <div
                                        className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors cursor-pointer"
                                        onClick={() => {
                                            // Filter auf alle freigeschalteten setzen
                                            setSelectedBadge(null);
                                            setTimeout(() => setFilter('unlocked'), 100);
                                        }}
                                    >
                                        +{selectedBadge.xp_reward} XP erhalten
                                    </div>
                                    {selectedBadge.unlocked_at && (
                                        <p className="text-xs text-gray-400 dark:text-gray-500">
                                            {new Date(selectedBadge.unlocked_at).toLocaleDateString('de-DE', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </p>
                                    )}
                                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 flex justify-center">
                                        <button
                                            onClick={async () => {
                                                if (downloadingCert) return;
                                                setDownloadingCert(true);
                                                const token = localStorage.getItem('authToken');
                                                await nativeDownload(
                                                    `/api/profile/certificate/achievement/${selectedBadge.id}/`,
                                                    token,
                                                    (msg) => alert(msg)
                                                );
                                                setDownloadingCert(false);
                                            }}
                                            disabled={downloadingCert}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${downloadingCert ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-plantlix-100 text-plantlix-800 hover:bg-plantlix-200 dark:bg-plantlix-900/30 dark:text-plantlix-400 dark:hover:bg-plantlix-900/50'}`}
                                        >
                                            {downloadingCert ? (
                                                 <><span className="w-4 h-4 rounded-full border-2 border-plantlix-500 border-t-transparent animate-spin"></span> Lädt ...</>
                                            ) : (
                                                 <>📜 Urkunde herunterladen</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                                    <div>
                                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Fortschritt</p>
                                        {selectedBadge.progress > 0 ? (
                                            <>
                                                <div
                                                    className="w-full mb-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                                                    onClick={() => {
                                                        // Filter auf gesperrte setzen
                                                        setSelectedBadge(null);
                                                        setTimeout(() => setFilter('locked'), 100);
                                                    }}
                                                >
                                                    <DancingDotsProgress
                                                        percentage={selectedBadge.progress}
                                                        height="h-1"
                                                    />
                                                </div>
                                                <p className="text-xs text-gray-600 dark:text-gray-400">{selectedBadge.progress}% erreicht</p>
                                            </>
                                        ) : (
                                            <p className="text-xs text-gray-500 dark:text-gray-500">Noch nicht gestartet</p>
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">Belohnung</p>
                                        <p
                                            className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors cursor-pointer"
                                            onClick={() => {
                                                // Filter auf alle setzen
                                                setSelectedBadge(null);
                                                setTimeout(() => setFilter('all'), 100);
                                            }}
                                        >
                                            +{selectedBadge.xp_reward} XP
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Zusätzliche Aktionen im Modal */}
                            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 flex gap-2 justify-center">
                                <button
                                    onClick={() => {
                                        setSelectedBadge(null);
                                        setFilter(selectedBadge.unlocked ? 'unlocked' : 'locked');
                                    }}
                                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors underline"
                                >
                                    {selectedBadge.unlocked ? 'Alle freigeschalteten' : 'Alle gesperrten'} anzeigen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Badges;
